import {getStoreCardImageView, type StoreProductCardImageInput} from '../components/product-card-image/store-card-image-recovery';
import {getEffectiveStorePrice} from '../pricing';
import type {IStorePage_ViewProduct} from '../storepage.def';

/** Marks the ItemList this store page owns. Unrelated JSON-LD scripts do not get this attribute. */
export const STORE_PAGE_SCHEMA_ATTRIBUTE = 'data-store-page-schema';

export type StoreSchemaContext = {
    cdnDomain?: string | null;
    pageHref: string;
    priceSlashActive: boolean;
};

/**
 * Absolute http(s) URL the product card link resolves to.
 * Relative paths and numeric strings are resolved against the current page.
 * Blank values are omitted. Non-http(s) schemes are omitted.
 */
export function getCanonicalProductUrl(url: unknown, pageHref: string): string | undefined {
    const raw = urlText(url);
    if (raw === undefined) return undefined;
    if (typeof pageHref !== 'string' || pageHref.trim() === '') return undefined;

    try {
        const resolved = new URL(raw, pageHref);
        if (resolved.protocol !== 'http:' && resolved.protocol !== 'https:') return undefined;
        return resolved.href;
    } catch {
        return undefined;
    }
}

/** The `<img src>` the card would use before any load failure. WebP stays a `<source>` enhancement. */
export function getStoreCardImageUrl(input: StoreProductCardImageInput): string | undefined {
    const view = getStoreCardImageView(input, []);
    if (view.type === 'preferred') return view.originalSrc;
    if (view.type === 'original' || view.type === 'thumbnail') return view.src;
    return undefined;
}

/** Offer amount for one visible card. Missing or non-finite MSRP is omitted rather than guessed. */
export function getSchemaOfferPrice(product: IStorePage_ViewProduct, priceSlashActive: boolean): number | undefined {
    const msrp = product?.fsi?.priceMSRPDec;
    if (typeof msrp !== 'number' || !Number.isFinite(msrp)) return undefined;
    return getEffectiveStorePrice(msrp, priceSlashActive);
}

export function buildStorePageItemList(products: readonly IStorePage_ViewProduct[], context: StoreSchemaContext) {
    return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: products.map((product, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: buildProductNode(product, context),
        })),
    };
}

/**
 * Removes this page's product ItemList and, when there are visible products, writes one replacement.
 * An empty list removes the owned script and does not write a new one.
 * Legacy server lists are recognized by parsed JSON, including compact JSON. Other schema types stay.
 */
export function replaceOwnedStoreSchema(doc: Document, products: readonly IStorePage_ViewProduct[], context: StoreSchemaContext): void {
    const owned = Array.from(doc.querySelectorAll(`script[${STORE_PAGE_SCHEMA_ATTRIBUTE}]`));
    owned.forEach(script => script.remove());

    const jsonScripts = Array.from(doc.querySelectorAll('script[type="application/ld+json"]'));
    jsonScripts.forEach(script => {
        if (script.hasAttribute(STORE_PAGE_SCHEMA_ATTRIBUTE)) return;
        if (isLegacyStoreProductItemList(script.textContent)) script.remove();
    });

    if (products.length === 0 || !doc.head) return;

    const script = doc.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(STORE_PAGE_SCHEMA_ATTRIBUTE, 'true');
    // Keep `<` out of the raw script text so a product name cannot close the tag.
    script.textContent = JSON.stringify(buildStorePageItemList(products, context)).replace(/</g, '\\u003c');
    doc.head.appendChild(script);
}

function buildProductNode(product: IStorePage_ViewProduct, context: StoreSchemaContext): Record<string, unknown> {
    const item: Record<string, unknown> = {'@type': 'Product'};
    const name = textValue(product?.Name);
    if (name !== undefined) item.name = name;

    const url = getCanonicalProductUrl(product?.fsi?.URL, context.pageHref);
    if (url !== undefined) item.url = url;

    const image = getStoreCardImageUrl({
        productId: product?.ProductID,
        name: typeof product?.Name === 'string' ? product.Name : '',
        customizationMethod: product?.CustomizationMethod,
        imageFull: product?.Image_Full,
        imageThumb: product?.Image_Thumb,
        imageItemId: product?.ImageItemID,
        cdnDomain: context.cdnDomain,
    });
    if (image !== undefined) item.image = image;

    const sku = textValue(product?.ItemID);
    if (sku !== undefined) item.sku = sku;

    const price = getSchemaOfferPrice(product, context.priceSlashActive);
    if (price !== undefined) {
        item.offers = {
            '@type': 'Offer',
            price,
            priceCurrency: 'USD',
        };
    }

    return item;
}

function textValue(value: unknown): string | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
}

function urlText(value: unknown): string | undefined {
    return textValue(value);
}

function isLegacyStoreProductItemList(scriptText: string | null): boolean {
    if (typeof scriptText !== 'string' || scriptText.trim() === '') return false;

    let parsed: unknown;
    try {
        parsed = JSON.parse(scriptText);
    } catch {
        return false;
    }

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
    const node = parsed as Record<string, unknown>;
    if ('@graph' in node) return false;
    if (node['@type'] !== 'ItemList') return false;
    if (!Array.isArray(node.itemListElement) || node.itemListElement.length === 0) return false;

    return node.itemListElement.every(element => {
        if (!element || typeof element !== 'object' || Array.isArray(element)) return false;
        const listItem = element as Record<string, unknown>;
        const product = listItem.item;
        if (!product || typeof product !== 'object' || Array.isArray(product)) return false;
        return listItem['@type'] === 'ListItem' && (product as Record<string, unknown>)['@type'] === 'Product';
    });
}
